import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import MetricIconPreview from "../metric/MetricIconPreview";
import {
  DEFAULT_DISPLAY_ORDER,
  DISPLAY_ORDER_MIN,
  DISPLAY_ORDER_STEP,
  METRIC_CATEGORY,
  METRIC_ICON_OPTIONS,
  getMetricCategory,
} from "../metric/metricOptions";

const useStyles = makeStyles(() => ({
  content: {
    display: "flex",
    flexDirection: "column",
    minWidth: "320px",
  },
  input: {
    margin: "10px 0",
  },
  categoryLabel: {
    margin: "0 0 10px",
    opacity: 0.75,
  },
  iconMenuItem: {
    minWidth: "40px",
    color: "#E00186",
  },
  iconSelectValue: {
    display: "flex",
    alignItems: "center",
    minHeight: "24px",
  },
  saveButton: {
    color: "#E00186",
    border: "1px solid #E00186",
    "&:hover": {
      color: "#fff",
      backgroundColor: "#E00186",
      border: "1px solid #E00186",
    },
  },
}));

const buildMetricFormState = (baseContentData, category, defaultDisplayOrder) => {
  const hasExistingMetric = baseContentData._id || baseContentData.id;
  const resolvedCategory = getMetricCategory(
    hasExistingMetric ? baseContentData.category : category
  );

  return {
    category: resolvedCategory,
    title: baseContentData.title || "",
    value: baseContentData.value || "",
    text: baseContentData.text || "",
    icon: baseContentData.icon || METRIC_ICON_OPTIONS[0].value,
    displayOrder:
      baseContentData.displayOrder ?? defaultDisplayOrder ?? DEFAULT_DISPLAY_ORDER,
  };
};

// Renders in a Dialog on top of the metrics list - there is no separate
// create/edit page or route for these items. A handful of stat tiles per
// category doesn't warrant full-page navigation for every add/edit.
export default ({
  open,
  onClose,
  title,
  onSubmit,
  isLoading,
  baseContentData = {},
  category,
  defaultDisplayOrder,
}) => {
  const classes = useStyles();
  const [state, setState] = useState(() =>
    buildMetricFormState(baseContentData, category, defaultDisplayOrder)
  );

  useEffect(() => {
    if (open) {
      setState(buildMetricFormState(baseContentData, category, defaultDisplayOrder));
    }
  }, [
    open,
    baseContentData._id,
    baseContentData.id,
    baseContentData.category,
    category,
    defaultDisplayOrder,
  ]);

  const handleChange = (event) => {
    const { id, name, value } = event.target;
    setState({ ...state, [id || name]: value });
  };

  const handleSubmit = () => {
    onSubmit(state);
  };

  const isReach = state.category === METRIC_CATEGORY.REACH;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title || "Metric Form"}</DialogTitle>
      <DialogContent className={classes.content}>
        <Typography variant="subtitle1" className={classes.categoryLabel}>
          Category: {isReach ? "Reach" : "Impact"}
        </Typography>
        {isReach ? (
          <>
            <TextField
              id="title"
              label="Title"
              variant="outlined"
              value={state.title}
              onChange={handleChange}
              className={classes.input}
              required
            />
            <TextField
              id="value"
              label="Value"
              value={state.value}
              onChange={handleChange}
              variant="outlined"
              className={classes.input}
              required
            />
          </>
        ) : (
          <>
            <TextField
              id="text"
              label="Text"
              multiline
              rows={4}
              value={state.text}
              onChange={handleChange}
              variant="outlined"
              className={classes.input}
              required
            />
            <TextField
              id="icon"
              name="icon"
              label="Icon"
              select
              value={state.icon}
              onChange={handleChange}
              variant="outlined"
              className={classes.input}
              required
              SelectProps={{
                renderValue: (selected) => (
                  <span className={classes.iconSelectValue}>
                    <MetricIconPreview iconKey={selected} showLabel />
                  </span>
                ),
              }}
            >
              {METRIC_ICON_OPTIONS.map((iconOption) => {
                const IconComponent = iconOption.IconComponent;

                return (
                  <MenuItem key={iconOption.value} value={iconOption.value}>
                    <ListItemIcon className={classes.iconMenuItem}>
                      <IconComponent fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={iconOption.label}
                      secondary={iconOption.description}
                    />
                  </MenuItem>
                );
              })}
            </TextField>
          </>
        )}
        <TextField
          id="displayOrder"
          label="Display Order"
          type="number"
          value={state.displayOrder}
          onChange={handleChange}
          variant="outlined"
          className={classes.input}
          inputProps={{
            min: DISPLAY_ORDER_MIN,
            step: DISPLAY_ORDER_STEP,
          }}
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={handleSubmit}
          disabled={isLoading}
          className={classes.saveButton}
        >
          {isLoading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
